import SaveResetButtons from "@/components/SaveResetButtons";
import { WELCOME_PRIVACY_POLICY_URL, WELCOME_TERMS_OF_USE_URL } from "@/lib";
import {
  useLoggedInUser,
  useRefetchUser,
  useSnackbarStore,
} from "@/store/zustandStore";
import styles from "@/styles/UserConsents.module.scss";
import { ConsentType, NotificationType } from "@/types";
import { Checkbox } from "@app-components";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { createFileRoute } from "@tanstack/react-router";
import { updateConsent } from "@/api";
import { usePrompt } from "@/hooks/usePrompt";
import { notifyApiError } from "@/lib";

export const Route = createFileRoute("/_portal/settings/consents/")({
  component: UserConsents,
});

function UserConsents() {
  const { t, i18n } = useTranslation();
  const { addNotification } = useSnackbarStore();
  const user = useLoggedInUser();
  const [generalConsent, setGeneralConsent] = useState(!!user?.generalConsent);
  const [loading, setLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const refetchedUser = useRefetchUser();

  usePrompt({
    isOpen: hasUnsavedChanges,
  });

  const handleSaveConsents = async () => {
    setLoading(true);
    try {
      const res = await updateConsent(user.userId, [
        { type: ConsentType.GENERAL, consented: generalConsent },
      ]);
      addNotification(
        t(`NOTIFICATIONS.${res.data.code}`, {
          entity: t("PAGES.WELCOME.CONSENTS.HEADER").toLowerCase(),
        }),
        NotificationType.SUCCESS,
      );
      refetchedUser();
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error(error);
      notifyApiError(error, addNotification, t, {
        properties: {
          entity: t("PAGES.WELCOME.CONSENTS.HEADER").toLowerCase(),
        },
        fallbackKey: "UPDATE_FAILED",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="pageHeader">
        <h1>{t("PAGES.WELCOME.CONSENTS.HEADER")}</h1>
        <p>
          {t("PAGES.WELCOME.YOUR_CHOICE")}, {t("PAGES.WELCOME.YOU_LOSE")}
        </p>
      </div>
      <div className={styles.consents}>
        <div className={styles.consent}>
          <Checkbox
            checked={generalConsent}
            onChange={() => {
              setGeneralConsent(!generalConsent);
              setHasUnsavedChanges(true);
            }}
            disabled={!!loading}
            label={
              <div className={styles.consentLabel}>
                <Trans
                  i18nKey="PAGES.WELCOME.CONSENTS.GENERAL"
                  components={{
                    bold: <strong></strong>,
                    paragraph: <p></p>,
                  }}
                ></Trans>
              </div>
            }
          />
        </div>
        <p>
          <Trans
            i18nKey="PAGES.SETTINGS.DISCLAIMER"
            components={{
              terms: (
                <a
                  href={WELCOME_TERMS_OF_USE_URL(i18n.language)}
                  target="_blank"
                ></a>
              ),
              policy: (
                <a
                  href={WELCOME_PRIVACY_POLICY_URL(i18n.language)}
                  target="_blank"
                ></a>
              ),
            }}
          ></Trans>
        </p>
        <SaveResetButtons
          showButtons={hasUnsavedChanges}
          loading={loading}
          onReset={() => setGeneralConsent(user.generalConsent)}
          onSave={handleSaveConsents}
          disabledSubmit={!hasUnsavedChanges}
        />
      </div>
    </>
  );
}
